<template>
    <div class="flex flex-col gap-10 border border-border bg-white p-4 dark:bg-card">
        <!-- Keys -->
        <div class="flex flex-col gap-10 lg:flex-row lg:items-start">
            <!-- Public key -->
            <div class="flex min-w-0 flex-1 flex-col gap-4">
                <div class="flex items-center gap-3">
                    <span class="text-sm text-muted-foreground">Public API key</span>
                    <span class="inline-flex items-center justify-center border border-success bg-surface-success px-2 py-0.5 text-[10px] font-medium leading-[10px] text-success">
                        Safe to share
                    </span>
                </div>
                <div class="flex items-center justify-between gap-2 border border-border bg-surface-card py-2 pl-4 pr-2 dark:bg-muted">
                    <span class="truncate font-mono text-base text-foreground">
                        {{ apiToken.token }}
                    </span>
                    <div class="flex shrink-0 items-center gap-1">
                        <CopyButtonV2
                            :value="apiToken.token"
                            label="Copy public key"
                        />
                    </div>
                </div>
            </div>

            <!-- Secret key -->
            <div class="flex min-w-0 flex-1 flex-col gap-4">
                <div class="flex items-center gap-3">
                    <span class="text-sm text-muted-foreground">Secret key</span>
                    <span class="inline-flex items-center justify-center border border-new-red-500 bg-new-red-100 px-2 py-0.5 text-[10px] font-medium leading-[10px] text-new-red-500">
                        Never expose
                    </span>
                </div>
                <div class="flex items-center justify-between gap-2 border border-border bg-surface-card py-2 pl-4 pr-2 dark:bg-muted">
                    <span class="truncate font-mono text-base text-foreground">
                        {{ secretKeyRevealed ? secretKey : secretKey.replace(/./g, '•') }}
                    </span>
                    <div class="flex shrink-0 items-center gap-1">
                        <CopyButtonV2
                            :value="secretKey"
                            :disabled="!secretKeyRevealed"
                            label="Copy secret key"
                        />
                        <button
                            :class="iconBtn"
                            :disabled="secretKeyRevealed || secretKeySeen"
                            aria-label="Reveal secret key"
                            @click="revealSecretKey"
                        >
                            <Eye v-if="!secretKeyRevealed" class="size-4" />
                            <EyeOff v-else class="size-4" />
                        </button>
                        <button :class="iconBtn" aria-label="Regenerate secret key" @click="regenerateDialogOpen = true">
                            <RotateCcw class="size-4" />
                        </button>
                    </div>
                </div>

                <!-- Warning -->
                <div class="flex items-center gap-2 bg-warning-100 px-3 py-2">
                    <TriangleAlert class="size-4 shrink-0 text-warning" />
                    <p class="text-xs leading-4 text-warning">
                        {{
                            secretKeySeen
                                ? 'Secret key already viewed — regenerate to see a new one.'
                                : 'Keep your secret key secure. It can only be viewed once.'
                        }}
                    </p>
                </div>
            </div>
        </div>

        <!-- .env.local -->
        <div class="w-full border border-border">
            <div class="flex items-center justify-between border-b border-border bg-surface-card py-1 pl-2 pr-1 dark:bg-muted">
                <div class="flex items-center gap-1.5">
                    <FileCode class="size-4 text-muted-foreground" />
                    <span class="font-mono text-sm text-muted-foreground">.env.local</span>
                </div>
                <CopyButtonV2 :value="envCode" label="Copy .env" />
            </div>
            <div class="flex gap-3 overflow-x-auto bg-surface-card px-4 pb-10 pt-4 font-mono text-sm dark:bg-muted">
                <div class="select-none text-new-neutral-400">
                    <div>1</div>
                    <div>2</div>
                </div>
                <div class="min-w-0 text-foreground">
                    <div class="whitespace-nowrap">TOPOL_PUBLIC_KEY={{ apiToken.token }}</div>
                    <div class="whitespace-nowrap">
                        TOPOL_SECRET_KEY={{ secretKeyRevealed ? secretKey : '•••••••••••••••••••' }}
                    </div>
                </div>
            </div>
        </div>

        <!-- Regenerate confirmation -->
        <Dialog
            :open="regenerateDialogOpen"
            @update:open="(value) => !value && (regenerateDialogOpen = false)"
        >
            <DialogContent class="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Regenerate secret key</DialogTitle>
                </DialogHeader>

                <div class="space-y-2">
                    <p class="text-sm text-muted-foreground">
                        A new secret key will be generated and the current one will stop
                        working immediately. Any integrations using the old key must be
                        updated.
                    </p>
                    <div class="flex items-center gap-2 bg-warning-100 px-3 py-2">
                        <TriangleAlert class="size-4 shrink-0 text-warning" />
                        <p class="text-xs leading-4 text-warning">
                            This action cannot be undone.
                        </p>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="ghost"
                        size="lg"
                        @click="regenerateDialogOpen = false"
                    >
                        Cancel
                    </Button>
                    <Button variant="destructive" size="lg" @click="confirmRegenerate">
                        Regenerate
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    </div>
</template>

<script lang="ts" setup>
import { PropType, ref, onMounted, computed } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';
import { route } from 'ziggy-js';
import { toast } from 'vue-sonner';
import IToken from '@/types/Plugin/IToken';
import { Button } from '@/components/ui/button';
import { CopyButtonV2 } from '@/components/ui/copy-button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    Eye,
    EyeOff,
    RotateCcw,
    TriangleAlert,
    FileCode,
} from 'lucide-vue-next';

const props = defineProps({
    apiToken: {
        type: Object as PropType<IToken>,
        required: true,
    },
});

const iconBtn =
    'flex items-center justify-center p-2 text-new-neutral-400 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-new-neutral-400';

const defaultSecretKey = 'e1sbRX0Mpx6FyhmM4KPx8c3xw2uCOSXnSUx7XRJ64s9gNB8XdeMKLJQ4xt';
const secretKey = ref(defaultSecretKey);
const secretKeyRevealed = ref(false);
const secretKeySeen = ref(false);
const regenerateDialogOpen = ref(false);

const envCode = computed(() => {
    return `TOPOL_PUBLIC_KEY=${props.apiToken.token}\nTOPOL_SECRET_KEY=${secretKeyRevealed.value ? secretKey.value : '•••••••••••••'}`;
});

onMounted(() => {
    fetchSecretKeySeen();
});

const revealSecretKey = async () => {
    const secret = await fetchSecretKey();

    if (secret === null) {
        secretKeyRevealed.value = false;
        secretKey.value = defaultSecretKey;
    } else {
        secretKey.value = secret;
        secretKeyRevealed.value = true;
    }
};

const fetchSecretKeySeen = async () => {
    try {
        const res = await axios.get(
            route('api-tokens.secret-token.seen.show', {
                apiToken: props.apiToken.id,
            })
        );

        secretKeySeen.value = res.data.seen;
    } catch (error) {
        console.error('Error fetching secret key seen status:', error);
    }
};

const fetchSecretKey = async () => {
    try {
        const res = await axios.get(
            route('api-tokens.secret-token.show', {
                apiToken: props.apiToken.id,
            })
        );
        return res.data.secret;
    } catch (error) {
        console.error('Error fetching secret key:', error);
    }
};

const regenerateSecretKey = () => {
    router.post(
        route('api-tokens.secret-token.store', {
            apiToken: props.apiToken.id,
        }),
        {},
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                toast.success('Secret token regenerated');

                secretKey.value = defaultSecretKey;
                secretKeyRevealed.value = false;

                fetchSecretKeySeen();
            },
        }
    );
};

const confirmRegenerate = () => {
    regenerateDialogOpen.value = false;
    regenerateSecretKey();
};
</script>
